import { useState, useRef } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  ColumnDef,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Icon,
  Tooltip,
} from '@mui/material';
import { SalesOrderSource } from '@erp/shared-types';
import MDBox from 'src/components/md/MDBox';
import ErrorMessage from 'src/components/errorMessage/ErrorMessage';
import { useNavigate } from '@tanstack/react-router';
import {
  LineItem,
  SalesOrderDraftStatus,
} from 'src/types/application/SalesOrderDraftDTO';
import { FilterValues } from '../SalesOrderFilters';
import useSalesOrderDraftsV2, {
  SalesOrderDraftPreview,
} from 'src/api/accounts-receivable/sales-order-drafts/useSalesOrderDraftsV2';
import MDTypography from 'src/components/md/MDTypography';
import CustomerPill from 'src/components/customer/CustomerPill';
import LocationPill from 'src/components/location/LocationPill';
import SourceBadge from 'src/components/source-badge/SourceBadge';
import { SalesOrderDraftStatusBadge } from 'src/components/sales-order-draft-status-badge/SalesOrderDraftStatusBadge';
import { useFeatureFlag } from '@/integrations/feature-flags/feature-flags';
import { FEATURE_FLAGS } from '@erp/shared-types';

const missingMessage = (
  <MDBox display="flex" alignItems="center">
    <Icon sx={{ mr: '5px' }} color="warning">
      warning
    </Icon>
    <MDTypography variant="caption" color="warning" fontWeight="bold">
      {/* Missing */}
    </MDTypography>
  </MDBox>
);

const OriginIdBadge: React.FC<{ originId: string }> = ({ originId }) => {
  const [copied, setCopied] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    navigator.clipboard.writeText(originId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <MDBox
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        alignSelf: 'flex-start',
        gap: 0.5,
      }}
    >
      <Tooltip
        title="Copied!"
        arrow
        placement="top"
        open={copied}
        disableHoverListener
      >
        <MDBox
          onClick={handleCopy}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: 0.75,
            py: 0.25,
            borderRadius: '4px',
            backgroundColor: copied
              ? 'rgba(46, 125, 50, 0.1)'
              : 'rgba(0, 0, 0, 0.04)',
            border: copied
              ? '1px solid rgba(46, 125, 50, 0.3)'
              : '1px solid rgba(0, 0, 0, 0.1)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: copied
                ? 'rgba(46, 125, 50, 0.12)'
                : 'rgba(0, 0, 0, 0.06)',
              borderColor: copied
                ? 'rgba(46, 125, 50, 0.4)'
                : 'rgba(0, 0, 0, 0.15)',
            },
          }}
        >
          <MDTypography
            variant="caption"
            color={copied ? 'success.main' : 'text.secondary'}
            sx={{
              fontSize: '0.7rem',
              fontFamily: 'monospace',
              fontWeight: 500,
            }}
          >
            #{originId}
          </MDTypography>
        </MDBox>
      </Tooltip>
      {(isHovered || copied) && (
        <MDBox
          onClick={handleCopy}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            opacity: copied ? 1 : 0.6,
            transition: 'opacity 0.2s ease',
            '&:hover': {
              opacity: 1,
            },
          }}
        >
          {copied ? (
            <Icon sx={{ fontSize: '0.875rem', color: 'success.main' }}>
              check
            </Icon>
          ) : (
            <Icon sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              content_copy
            </Icon>
          )}
        </MDBox>
      )}
    </MDBox>
  );
};

interface Props {
  filters?: FilterValues;
  isReadyForApproval?: boolean;
}

const SalesOrderDraftsTable: React.FC<Props> = ({
  filters = {},
  isReadyForApproval,
}) => {
  const navigate = useNavigate();

  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Use a ref to track if we're in the middle of a pagination change
  const isPaginating = useRef(false);

  // Feature flag to filter by customer's EBS Org ID
  const { enabled: isEbsOrgFilterEnabled } = useFeatureFlag(
    FEATURE_FLAGS['sales-order-drafts-ebs-org-filter-enabled'],
    false,
  );

  // TODO: The customerEbsOrgId value (95) is currently hardcoded for a specific customer deployment.
  // This should be read from organization config/settings instead of being hardcoded.
  // The feature flag gates whether this filter is applied at all.
  const { data, isLoading, error } = useSalesOrderDraftsV2({
    page: pageIndex,
    pageSize: pageSize,
    status: filters.statuses,
    customerId: filters.customerIds,
    excludedCustomerId: filters.excludedCustomerIds,
    source: filters.sources,
    watchStatus: filters.watchStatus,
    purchaseOrderNumber: filters.purchaseOrderNumber,
    isReadyForApproval,
    ...(isEbsOrgFilterEnabled ? { customerEbsOrgId: 95 } : {}),
  });

  const handlePageChange = (_: unknown, newPage: number) => {
    // MUI pagination is 0-based, but our API is 1-based
    isPaginating.current = true;
    setPageIndex(newPage + 1);
  };

  // Handle row click - navigate to full-screen detail page
  const handleRowClick = (draftId: string) => {
    navigate({
      to: '/sales-orders/draft/$draftId',
      params: { draftId },
    });
  };

  const columns: ColumnDef<SalesOrderDraftPreview>[] = [
    {
      accessorKey: 'index',
      header: '#',
      cell: (info) => {
        const rowIndex = info.row.index;
        const absoluteIndex = (pageIndex - 1) * pageSize + rowIndex + 1;
        return <MDTypography variant="caption">{absoluteIndex}</MDTypography>;
      },
      size: 50,
      minSize: 50,
      maxSize: 50,
    },
    {
      accessorKey: 'purchaseOrderNumber',
      header: 'PO Number',
      cell: (info) => {
        const row = info.row.original;
        const poNumber = info.getValue() as string;

        return (
          <MDBox>
            <MDTypography variant="caption">{poNumber || '-'}</MDTypography>
            {row.source && (
              <SourceBadge source={row.source as SalesOrderSource} />
            )}
          </MDBox>
        );
      },
      minSize: 120,
      maxSize: 180,
    },
    {
      id: 'customer',
      header: 'Customer',
      cell: (info) => {
        const row = info.row.original;
        const { customerId, suggestedCustomer, suggestedCustomerRejected } =
          row;

        const scrollableWrapper = (content: React.ReactNode) => (
          <MDBox
            sx={{
              overflowX: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            {content}
          </MDBox>
        );

        if (customerId) {
          return scrollableWrapper(
            <CustomerPill customerId={customerId} showERPDisplayName={true} />,
          );
        }

        if (suggestedCustomer && !suggestedCustomerRejected) {
          return scrollableWrapper(
            <CustomerPill customer={{ name: suggestedCustomer.name }} />,
          );
        }

        return (
          <MDBox display="flex" alignItems="center">
            <Icon sx={{ mr: '5px' }} color="error">
              warning
            </Icon>
            <MDTypography variant="caption" color="error">
              Customer Required
            </MDTypography>
          </MDBox>
        );
      },
      minSize: 150,
      maxSize: 250,
    },
    {
      accessorKey: 'lineItems',
      header: 'Order',
      cell: (info) => {
        const value = info.getValue() as LineItem[];

        if (!value?.length) return missingMessage;

        const firstItem = value[0];
        const itemName = firstItem.customerDescription || 'N/A';

        return (
          <MDTypography
            variant="caption"
            sx={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value.length} item{value.length !== 1 ? 's' : ''} - {itemName}
            {value.length > 1 ? '...' : ''}
          </MDTypography>
        );
      },
      minSize: 150,
    },
    {
      accessorKey: 'totalAmount',
      header: 'Amount',
      cell: (info) => {
        const value = info.getValue() as string;
        const lineItems = info.row.original.lineItems || [];

        // Try to get amount from totalAmount field
        if (value && !isNaN(parseFloat(value))) {
          return (
            <MDTypography variant="caption">
              $
              {parseFloat(value).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </MDTypography>
          );
        }

        // Calculate from line items if available
        const calculatedTotal = lineItems.reduce((sum, item) => {
          if (item.amount !== undefined) {
            return sum + item.amount;
          }
          if (item.quantity !== undefined && item.unitPrice !== undefined) {
            return sum + item.quantity * item.unitPrice;
          }
          return sum;
        }, 0);

        return calculatedTotal > 0 ? (
          <MDTypography variant="caption">
            $
            {calculatedTotal.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </MDTypography>
        ) : (
          <MDTypography variant="caption" color="text.secondary">
            -
          </MDTypography>
        );
      },
      size: 100,
      minSize: 80,
      maxSize: 120,
    },
    {
      accessorKey: 'shipToLocation',
      header: 'Destination',
      cell: (info) => {
        const value = info.getValue() as {
          id: string;
          locationName: string;
        } | null;
        const draft = info.row.original;
        const customerId = draft.customerId || draft.suggestedCustomerId;

        return value && customerId ? (
          <MDBox
            sx={{
              overflowX: 'auto',
              scrollbarWidth: 'none', // Firefox
              '&::-webkit-scrollbar': {
                display: 'none', // Chrome, Safari
              },
            }}
          >
            <LocationPill
              location={{
                name: value.locationName,
                address: null,
              }}
            />
          </MDBox>
        ) : (
          missingMessage
        );
      },
      size: 100,
      minSize: 90,
      maxSize: 140,
    },
    {
      accessorKey: 'receivedDate',
      header: 'Received',
      cell: (info) => {
        const value = info.getValue() as Date | string;
        if (!value) return missingMessage;

        const date = typeof value === 'string' ? new Date(value) : value;
        const dateStr = date.toLocaleDateString();
        const timeStr = date.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        });

        return (
          <MDBox>
            <MDTypography variant="caption" sx={{ display: 'block' }}>
              {dateStr}
            </MDTypography>
            <MDTypography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', fontSize: '0.7rem' }}
            >
              {timeStr}
            </MDTypography>
          </MDBox>
        );
      },
      size: 180,
      minSize: 180,
      maxSize: 180,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: (info) => {
        const value = info.getValue() as SalesOrderDraftStatus;
        const row = info.row.original;
        const originId = row.originId;

        return (
          <MDBox display="flex" flexDirection="column" gap={0.5}>
            <MDBox sx={{ alignSelf: 'flex-start' }}>
              <SalesOrderDraftStatusBadge
                status={value}
                isReadyForApproval={row.isReadyForApproval}
              />
            </MDBox>
            {originId && <OriginIdBadge originId={originId} />}
          </MDBox>
        );
      },
      size: 130,
      minSize: 100,
      maxSize: 150,
    },
  ];

  const table = useReactTable({
    data: data?.edges || [],
    columns,
    pageCount: data?.pageInfo?.totalPages || -1,
    manualPagination: true,
    state: {
      pagination: {
        pageIndex: pageIndex - 1,
        pageSize,
      },
    },
    onPaginationChange: (updater) => {
      const next =
        typeof updater === 'function'
          ? updater({ pageIndex: pageIndex - 1, pageSize })
          : updater;
      setPageIndex(next.pageIndex + 1);
      setPageSize(next.pageSize);
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  if (isLoading && !isPaginating.current) {
    return (
      <MDBox display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </MDBox>
    );
  }

  if (error) {
    return (
      <MDBox display="flex" justifyContent="center" p={3}>
        <ErrorMessage error={error} />
      </MDBox>
    );
  }

  // Column width percentages (must sum to ~100%)
  const columnWidths = [
    '4%', // #
    '8%', // PO Number
    '18%', // Customer
    '18%', // Order
    '6%', // Amount
    '23%', // Destination
    '7%', // Received
    '11%', // Status
  ];

  return (
    <MDBox>
      <TableContainer>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              {table.getHeaderGroups().map((headerGroup) =>
                headerGroup.headers.map((header, index) => {
                  return (
                    <TableCell
                      key={header.id}
                      sx={{
                        position: 'relative',
                        textAlign: 'left',
                        background: 'transparent',
                        boxShadow: 'none',
                        whiteSpace: 'nowrap',
                        width: columnWidths[index],
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : header.column.columnDef.header instanceof Function
                          ? header.column.columnDef.header(header.getContext())
                          : header.column.columnDef.header}
                    </TableCell>
                  );
                }),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
                onClick={() => handleRowClick(row.original.id)}
              >
                {row.getVisibleCells().map((cell, index) => {
                  return (
                    <TableCell
                      key={cell.id}
                      sx={{
                        color: '#7b809a',
                        fontSize: '0.875rem',
                        fontWeight: '400',
                        width: columnWidths[index],
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace:
                          cell.column.id === 'lineItems' ? 'normal' : 'nowrap',
                      }}
                    >
                      {cell.column.columnDef.cell instanceof Function
                        ? cell.column.columnDef.cell(cell.getContext())
                        : String(cell.getValue() ?? '')}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={data?.pageInfo?.totalCount || 0}
        page={pageIndex - 1}
        onPageChange={handlePageChange}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => {
          setPageSize(Number(e.target.value));
          setPageIndex(1);
        }}
        nextIconButtonProps={{
          disabled: !data?.pageInfo?.hasNextPage,
        }}
        backIconButtonProps={{
          disabled: !data?.pageInfo?.hasPreviousPage,
        }}
      />
    </MDBox>
  );
};

export default SalesOrderDraftsTable;
